import { Button, Popconfirm, Select, Tag } from 'antd';
import dayjs from 'dayjs';

export const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'unpublished', label: 'Unpublished' },
  { key: 'rejected', label: 'Rejected' },
];

export const STATUS_COLOR = {
  draft: 'gold',
  published: 'green',
  unpublished: 'default',
  rejected: 'red',
};

export const SORT_OPTIONS = [
  { value: 'news_date', label: 'News date' },
  { value: 'ingested_at', label: 'Ingested' },
  { value: 'relevance_score', label: 'Relevance score' },
  { value: 'headline', label: 'Headline' },
];

export const formatNewsDate = (value) =>
  value ? dayjs(value).format('DD MMM YYYY') : '—';

export function getNewsListColumns({ onOpen, onPublish, onUnpublish, canPublish, actionLoadingId }) {
  return [
    {
      title: 'Logo',
      key: 'logo',
      width: 64,
      render: (_, row) =>
        row.logo_url ? (
          <img
            src={row.logo_url}
            alt=""
            className="h-10 w-10 rounded object-cover border border-gray-100"
          />
        ) : (
          '—'
        ),
    },
    {
      title: 'Headline',
      dataIndex: 'headline',
      key: 'headline',
      render: (text, row) => (
        <button
          type="button"
          className="text-left text-blue-600 hover:underline"
          onClick={() => onOpen(row)}
        >
          {text}
        </button>
      ),
    },
    {
      title: 'Share',
      dataIndex: 'share_short_name',
      key: 'share_short_name',
      width: 100,
    },
    {
      title: 'Score',
      dataIndex: 'relevance_score',
      key: 'relevance_score',
      width: 80,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={STATUS_COLOR[status] || 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'News date',
      key: 'news_date',
      width: 120,
      render: (_, row) => formatNewsDate(row.published_at || row.ingested_at),
    },
    {
      title: 'Ingested',
      dataIndex: 'ingested_at',
      key: 'ingested_at',
      width: 120,
      render: (value) => formatNewsDate(value),
    },
    {
      title: 'Source',
      key: 'source',
      width: 90,
      render: (_, row) =>
        row.canonical_url ? (
          <a href={row.canonical_url} target="_blank" rel="noreferrer">
            Link
          </a>
        ) : (
          '—'
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, row) => {
        if (!canPublish) return '—';

        if (row.status === 'published') {
          return (
            <Popconfirm
              title="Unpublish this article?"
              onConfirm={() => onUnpublish(row)}
              okText="Unpublish"
            >
              <Button
                size="small"
                loading={actionLoadingId === row.id}
              >
                Unpublish
              </Button>
            </Popconfirm>
          );
        }

        if (row.status === 'draft' || row.status === 'unpublished') {
          return (
            <Popconfirm
              title="Publish this article?"
              onConfirm={() => onPublish(row)}
              okText="Publish"
            >
              <Button
                type="primary"
                size="small"
                loading={actionLoadingId === row.id}
              >
                Publish
              </Button>
            </Popconfirm>
          );
        }

        return '—';
      },
    },
  ];
}

export function buildShareFilterOptions(products) {
  const seen = new Set();
  const options = [{ value: '', label: 'All shares' }];

  for (const product of products) {
    const shortName = product.short_name?.trim();
    if (!shortName || seen.has(shortName.toLowerCase())) continue;
    seen.add(shortName.toLowerCase());
    options.push({ value: shortName, label: shortName });
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
}

export function buildProductSelectOptions(products) {
  return products
    .filter((p) => p.short_name?.trim())
    .map((p) => ({
      value: p.short_name,
      label: p.short_name,
      product: p,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function ProductSelectOption({ product }) {
  return (
    <div className="flex items-center gap-2">
      {product.logo_url ? (
        <img
          src={product.logo_url}
          alt=""
          className="h-6 w-6 rounded object-cover"
        />
      ) : (
        <span className="flex h-6 w-6 items-center justify-center rounded bg-gray-100 text-xs text-gray-500">
          {product.short_name?.substring(0, 2).toUpperCase()}
        </span>
      )}
      <span>
        {product.short_name}
        {product.name ? ` — ${product.name}` : ''}
      </span>
    </div>
  );
}
